import React, {useMemo} from "react";
import {
    useActiveProjectSelector,
} from "../../../../../store/selectors/project.selectors";
import {scaleTime, scaleLinear} from "@visx/scale";
import moment from "moment/moment";
import {GridColumns, GridRows} from "@visx/grid";
import {AxisLeft} from "@visx/axis";
import {LinePath} from "@visx/shape";
import {extent, bisector} from '@visx/vendor/d3-array'

export type ZoomIProps = {
    width: number;
    height: number;
    chartData: any[];
    baseline: any;
    comparator: any;
    setBaseline: (baseline: any) => void;
    setComparator: (comparator: any) => void;
};

const colors = [
    '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'
]
const bisectIndex = bisector((d: {x: number}) => d.x).left;


const margin = {top: 30, right: 10, bottom: 30, left: 10};
const accentColor = '#ed6c02';
const lineColor = '#4285f4';

export default function VersionChart({
     width,
     height,
     chartData,
     baseline,
     comparator,
     setBaseline,
     setComparator
}: ZoomIProps) {
    const activeProject = useActiveProjectSelector();
    const [hoverPosition, setHoverPosition] = React.useState<{ x: number; y: number, index: number } | null>(null);
    const [hoverLine, setHoverLine] = React.useState<number | null>(null);

    const pointData = useMemo(() => {
        let pointIndex = 0;
        let points: any[] = [];
        chartData.forEach((d: any, i) => {
            if (d.projectId) pointIndex = i;
            points.push({x: i, y: d.projectId ? 2 : 1, data: d, pointIndex});
        })
        return points;
    }, [chartData]);

    const data = useMemo(() => {
        let lines: any[] = [];
        let pointIndex = 0;
        let hoverLineData: any = null;
        chartData.forEach((version, index) => {
            if (version.projectId && chartData[index + 1] && !chartData[index + 1].projectId) {
                pointIndex = index;
                const lineData = chartData
                    .slice(index, chartData.length)
                    .map((d, i) => d.projectId && i !== 0 ? null : {x: index + i, y: d.projectId ? 2 : 1, pointIndex})
                    .filter(d => d !== null)
                lines.push(lineData)
                if (hoverLine !== null && hoverLine === pointIndex) {
                    hoverLineData = lineData;
                }
            }
            else if (version.projectId && chartData[index + 1] && chartData[index + 1].projectId) {
                pointIndex = index;
                lines.push([{x: index, y: 2, pointIndex}, {x: index + 1, y: 2, pointIndex}] )
            }
        });

        if (hoverLineData) {
            lines.push(hoverLineData);
        }

        return  {
            data: lines,
            lines: lines.map((lineData: [{x: number, y: number, pointIndex: number}], i) => (
                <LinePath
                    key={`line-${i}`}
                    data={lineData}
                    x={d => xScale(d!.x)}
                    y={d => yScale(d!.y)}
                    stroke={colors[lineData[0].pointIndex % colors.length]}
                    strokeWidth={3}
                />)),
        };
    }, [chartData, hoverLine]);

    const yScale = useMemo(() =>
        scaleLinear({
            range: [height - margin.bottom, margin.top],
            domain: [1, 2] as [number, number],
        }), [data])

    const xScale = useMemo(() =>
        scaleLinear({
            range: [margin.left, width - margin.right],
            domain: [0, chartData.length -1] as [number, number],
        }), [data])

    const handlePointSelect = (xPos: number) => {
        const selectedPoint = pointData[xPos];
        if (!baseline) {
            setBaseline({...selectedPoint, projectId: pointData[selectedPoint.pointIndex].data.projectId});
        } else if (!comparator && baseline.x < xPos) {
            setComparator({...selectedPoint, projectId: pointData[selectedPoint.pointIndex].data.projectId});
        } else if (xPos < baseline.x) {
            setBaseline({...selectedPoint, projectId: pointData[selectedPoint.pointIndex].data.projectId});
        } else {
            setComparator({...selectedPoint, projectId: pointData[selectedPoint.pointIndex].data.projectId});
        }
    }

    // const numMonths = useMemo(() => {
    //     if (data.length < 2) return 1;
    //     const start = data[0].date;
    //     const end = data[data.length - 1].date;
    //     return moment(end).diff(moment(start), 'months', true) + 1;
    // }, [data])

    // const dataPopup = () => {
    //     if (midValue && midValue.date && midValue.value && hoverPosition) {
    //         return (
    //             <div style={{
    //                 position: 'absolute',
    //                 top: height + yScale(midValue.value) - 120,
    //                 left: hoverPosition.x -150,
    //                 backgroundColor: 'white',
    //                 border: '1px solid #ccc',
    //                 padding: '5px',
    //                 fontSize: '10px',
    //                 pointerEvents: 'none'
    //             }}>
    //                 {/*<div>Date: {midValue.date.toDateString()}</div>*/}
    //                 <strong>Forecast: {midValue.value.toDateString()}</strong>
    //                 <div style={{color: 'rgb(15, 157, 88)'}}>(baseline: {baselineDate.toDateString()})</div>
    //             </div>
    //         )
    //     }
    //     return null;
    // }

    return <div>
        {data.lines.length > 0 &&
            <svg width={width} height={height}
                 onMouseMove={(e) =>
                     setHoverPosition({x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY, index: bisectIndex(pointData, xScale.invert(e.nativeEvent.offsetX))})}
                 onMouseLeave={() => setHoverPosition(null)}
                 onClick={(e) => handlePointSelect(bisectIndex(pointData, xScale.invert(e.nativeEvent.offsetX)))}
                 style={{cursor: 'pointer'}}
            >
                <rect
                    x={0}
                    y={0}
                    width={width}
                    height={height}
                    fill="white"
                />
                <GridColumns
                    top={0}
                    left={xScale(0.25)}
                    scale={xScale}
                    height={height}
                    strokeDasharray="1,3"
                    stroke={'#484643'}
                    strokeOpacity={0.8}
                    pointerEvents="none"
                    numTicks={pointData.length}
                />
                {baseline && <rect
                    x={0}
                    y={0}
                    width={xScale(baseline.x)}
                    height={height}
                    fill={'rgba(72, 70, 67, 0.2)'}
                />
                }
                {comparator && <rect
                    x={xScale(comparator.x)}
                    y={0}
                    width={xScale(chartData.length -1) - xScale(comparator.x) + 10}
                    height={height}
                    fill={'rgba(72, 70, 67, 0.2)'}
                />}
                {data.lines}
                {pointData.map((d, i) => (
                    <circle
                        key={`point-${i}`}
                        cx={xScale(d.x)}
                        cy={yScale(d.y)}
                        r={5}
                        fill={hoverPosition && hoverPosition.index === i ? colors[d.pointIndex % colors.length] : 'white'}
                        stroke={hoverLine && hoverLine <= d.pointIndex && !d.data.projectId ?
                            colors[hoverLine % colors.length] :
                        colors[d.pointIndex % colors.length]}
                        strokeWidth={2}
                        onClick={() => handlePointSelect(i)}
                        style={{cursor: 'pointer'}}
                        onMouseOver={() => setHoverLine(d.pointIndex)}
                        onMouseLeave={() => setHoverLine(null)}
                    />
                ))}
                <circle
                    cx={xScale(baseline ? baseline.x : pointData[0].x)}
                    cy={yScale(baseline ? baseline.y : pointData[0].y)}
                    r={10}
                    fill={colors[baseline ? baseline.pointIndex % colors.length : 0]}
                    stroke={'white'}
                    strokeWidth={2}
                />
                <circle
                    cx={xScale(comparator ? comparator.x : pointData[pointData.length -1].x)}
                    cy={yScale(comparator ? comparator.y : pointData[pointData.length -1].y)}
                    r={10}
                    fill={comparator ? colors[comparator.pointIndex % colors.length] : colors[pointData[pointData.length -1].pointIndex % colors.length]}
                    stroke={'white'}
                    strokeWidth={2}
                />
            </svg>}
        {hoverPosition && pointData[hoverPosition.index] && <div style={{
            position: 'absolute',
            top: height - 15,
            left: hoverPosition.x - 70,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            padding: '0px 5px',
            fontSize: '10px',
            pointerEvents: 'none',
            minHeight: '20px',
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
        }}>
            {pointData[hoverPosition.index] && pointData[hoverPosition.index].data.projectId ?
                `Version: ${pointData[hoverPosition.index].data.versionName} ${moment(pointData[hoverPosition.index].data.versionTimestamp.seconds * 1000).format('DD-MMM-YYYY HH:mm')}` :
                `Sprint update: ${moment(pointData[hoverPosition.index].data.versionTimestamp.seconds * 1000).format('DD-MMM-YYYY')}`}
        </div>}
        {/*{dataPopup()}*/}
    </div>
}
